// Channels workspace — publisher-facing drop-folder management (plan §7d;
// /pub/channels). A channel binds one watched SFTP/rsync folder to one collection:
// anything dropped there auto-publishes into that collection and is readable by
// whoever is already granted it (grants stay in Collections + Users → Manage
// grants). The page surfaces the drop path to hand to the publishing pipeline.
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { toast } from 'sonner';
import { FolderSync, Plus, X, Trash2, Copy, Check } from 'lucide-react';
import { apiFetch, ApiError } from '@/lib/api-client';
import { AppShell } from '@/components/AppShell';

interface PublisherOption {
  id: string;
  slug: string;
  name: string;
}

interface CollectionOption {
  id: string;
  slug: string;
  name: string;
  isDefault: boolean;
}

interface Channel {
  id: string;
  publisherId: string;
  label: string;
  kind: string;
  collectionId: string | null;
  collection: { id: string; slug: string; name: string; isDefault: boolean } | null;
  relPath: string | null;
  dropPath: string | null;
  isActive: boolean;
  lastSyncAt: string | null;
  createdAt: string;
}

export function Channels() {
  const { t } = useTranslation('channels');

  const [publishers, setPublishers] = useState<PublisherOption[] | null>(null);
  const [publisherId, setPublisherId] = useState('');
  const [channels, setChannels] = useState<Channel[] | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    apiFetch<{ data: PublisherOption[] }>('/pub/publishers')
      .then((res) => {
        setPublishers(res.data);
        setPublisherId((prev) => prev || res.data[0]?.id || '');
      })
      .catch(() => setPublishers([]));
  }, []);

  const load = useCallback(async () => {
    if (!publisherId) return;
    setChannels(null);
    try {
      const res = await apiFetch<{ data: Channel[] }>(`/pub/channels?publisherId=${publisherId}`);
      setChannels(res.data);
    } catch {
      setChannels([]);
    }
  }, [publisherId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function remove(c: Channel) {
    if (!window.confirm(t('confirmDelete', { name: c.label }))) return;
    try {
      await apiFetch(`/pub/channels/${c.id}`, { method: 'DELETE' });
      toast.success(t('toast.deleted'));
      void load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common:error'));
    }
  }

  if (publishers === null) {
    return (
      <AppShell>
        <div className="flex justify-center py-24">
          <div className="w-9 h-9 rounded-full border-[3px] border-slate-200 border-t-brand-rose animate-spin" />
        </div>
      </AppShell>
    );
  }

  if (publishers.length === 0) {
    return (
      <AppShell>
        <div className="text-center py-24 animate-fade-up">
          <p className="text-sm text-slate-500 max-w-md mx-auto">{t('noCategory')}</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mb-10 animate-fade-up">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl font-medium tracking-tight gradient-text pb-1">{t('title')}</h1>
            <p className="text-sm font-light text-slate-500 mt-1 max-w-xl">{t('subtitle')}</p>
          </div>
          <div className="flex items-end gap-3">
            {publishers.length > 1 && (
              <label className="text-xs font-semibold text-slate-500 flex flex-col gap-1.5">
                {t('categoryLabel')}
                <select value={publisherId} onChange={(e) => setPublisherId(e.target.value)} className="paper-input w-52">
                  {publishers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <button type="button" onClick={() => setCreateOpen(true)} className="btn-gradient">
              <Plus className="w-4 h-4" />
              {t('new')}
            </button>
          </div>
        </div>
      </div>

      {channels === null ? (
        <div className="flex justify-center py-24">
          <div className="w-9 h-9 rounded-full border-[3px] border-slate-200 border-t-brand-rose animate-spin" />
        </div>
      ) : channels.length === 0 ? (
        <div className="text-center py-24 animate-fade-up">
          <div className="inline-flex p-[2px] rounded-2xl bg-brand-gradient mb-5">
            <div className="p-4 rounded-2xl bg-white">
              <FolderSync className="w-8 h-8 text-brand-purple" />
            </div>
          </div>
          <p className="text-sm font-light text-slate-500 max-w-sm mx-auto">{t('empty')}</p>
        </div>
      ) : (
        <div className="space-y-2 animate-fade-up [animation-delay:0.08s]">
          {channels.map((c) => (
            <div
              key={c.id}
              className="flex items-start justify-between gap-4 rounded-xl bg-white border border-slate-200/80 shadow-card px-5 py-4 hover:border-brand-purple/30 transition-colors"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <FolderSync className="w-4 h-4 text-brand-purple/70 shrink-0" />
                  <p className="text-sm font-semibold text-ink truncate">{c.label}</p>
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{c.kind}</span>
                </div>
                <p className="text-xs font-light text-slate-500 mt-1">
                  {t('intoCollection')}{' '}
                  <span className="font-medium text-brand-purple/80">{c.collection?.name ?? '—'}</span>
                </p>
                {c.dropPath && <DropPath path={c.dropPath} />}
              </div>
              <button
                type="button"
                onClick={() => void remove(c)}
                title={t('delete')}
                className="p-2 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors shrink-0"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <CreateChannelDialog
        open={createOpen}
        publisherId={publisherId}
        onClose={() => setCreateOpen(false)}
        onDone={() => void load()}
      />
    </AppShell>
  );
}

function DropPath({ path }: { path: string }) {
  const { t } = useTranslation('channels');
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(path);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }
  return (
    <div className="mt-2 flex items-center gap-2">
      <code className="text-[11px] font-mono text-slate-500 bg-slate-50 border border-slate-100 rounded px-2 py-1 truncate max-w-md">
        {path}
      </code>
      <button
        type="button"
        onClick={() => void copy()}
        title={t('copyPath')}
        className="p-1.5 rounded-md text-slate-400 hover:text-brand-purple hover:bg-brand-purple/5 transition-colors shrink-0"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

function CreateChannelDialog({
  open,
  publisherId,
  onClose,
  onDone,
}: {
  open: boolean;
  publisherId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('channels');
  const [collections, setCollections] = useState<CollectionOption[]>([]);
  const [collectionId, setCollectionId] = useState('');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !publisherId) return;
    setLabel('');
    setCollectionId('');
    apiFetch<{ data: CollectionOption[] }>(`/pub/collections?publisherId=${publisherId}`)
      .then((res) => setCollections(res.data))
      .catch(() => setCollections([]));
  }, [open, publisherId]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (saving || !collectionId) return;
    setSaving(true);
    try {
      await apiFetch('/pub/channels', {
        method: 'POST',
        body: JSON.stringify({ publisherId, collectionId, ...(label.trim() ? { label: label.trim() } : {}) }),
      });
      toast.success(t('toast.created'));
      onClose();
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common:error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm animate-fade-in" />
        <Dialog.Content className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-md rounded-2xl bg-white shadow-2xl p-6 animate-dialog-in focus:outline-none max-h-[90dvh] overflow-y-auto custom-scrollbar">
          <div className="flex items-start justify-between gap-4 mb-1">
            <Dialog.Title className="text-base font-semibold text-ink flex items-center gap-2">
              <FolderSync className="w-4 h-4 text-brand-purple" />
              {t('dialog.title')}
            </Dialog.Title>
            <Dialog.Close className="p-1 rounded-md text-slate-400 hover:text-ink hover:bg-slate-100 transition-colors">
              <X className="w-4 h-4" />
            </Dialog.Close>
          </div>
          <Dialog.Description className="text-xs font-light text-slate-500 mb-4">{t('dialog.subtitle')}</Dialog.Description>
          <form onSubmit={(e) => void submit(e)} className="space-y-4">
            <label className="block">
              <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('dialog.collection')}</span>
              {collections.length === 0 ? (
                <p className="text-xs font-light text-slate-400">{t('dialog.noCollections')}</p>
              ) : (
                <select required value={collectionId} onChange={(e) => setCollectionId(e.target.value)} className="paper-input">
                  <option value="" disabled>
                    {t('dialog.collectionPlaceholder')}
                  </option>
                  {collections.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.isDefault ? t('common:uncategorized') : c.name}
                    </option>
                  ))}
                </select>
              )}
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('dialog.label')}</span>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={t('dialog.labelPlaceholder')}
                className="paper-input"
              />
            </label>
            <div className="flex justify-end gap-3 pt-1">
              <Dialog.Close type="button" className="btn-quiet">
                {t('common:cancel')}
              </Dialog.Close>
              <button type="submit" disabled={saving || !collectionId} className="btn-gradient">
                {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                {saving ? t('dialog.creating') : t('dialog.create')}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
